import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Category } from '@ui/src/components/accounts-selector/types';
import {
  Pagination,
  Response,
  GeneralLedgerTransaction,
  GeneralLedgerStatus,
  AccountingJobProps as AccountingJob,
} from '@ui/src';
import { glTransactionsListApi } from '../services';

import ROUTES from '../../../../routes';

export interface GeneralLedgerListState {
  transactions?: GeneralLedgerTransaction[];
  pagination?: Pagination;
  selectedTake?: number;
  accountCategories?: Category[];
  accountingJobs?: AccountingJob[];
  errorsList?: string;
}

const initialState: GeneralLedgerListState = {
  transactions: undefined,
};

export const generalLedgerListSlice = createSlice({
  name: 'generalLedgerList',
  initialState,
  reducers: {
    setSelectedTake: (state, action: PayloadAction<number>) => {
      state.selectedTake = action.payload;
    },

    clearGeneralLedgerListState: (state) => {
      state.transactions = undefined;
      state.pagination = undefined;
      state.selectedTake = undefined;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      glTransactionsListApi.endpoints.getGLAccountingJobs.matchPending,
      (state) => {
        state.accountingJobs = undefined;
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.getGLAccountingJobs.matchFulfilled,
      (state, action: PayloadAction<Response<AccountingJob>>) => {
        state.accountingJobs = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.getGLAccountingJobsStates.matchFulfilled,
      (state, action: PayloadAction<Response<AccountingJob>>) => {
        state.accountingJobs = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.getGeneralLedgerList.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.getGeneralLedgerList.matchRejected,
      (state, action: any) => {
        state.transactions = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.getGeneralLedgerList.matchFulfilled,
      (state, action: PayloadAction<Response<GeneralLedgerTransaction>>) => {
        const nextTransactions = action.payload.data.edges.map((item) => ({
          ...item.node,
          accountId: item.node.debitAccountId,
          deletedAt: item?.node?.deletedAt || '',
          url: generatePath(ROUTES.GENERAL_LEDGER_READ, {
            transactionId: item.node.id,
          }),
        }));

        state.transactions = nextTransactions;
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.createGLTransaction.matchFulfilled,
      (state, action: PayloadAction<{ data: GeneralLedgerTransaction }>) => {
        let nextTransactions: GeneralLedgerTransaction[] = [];

        if (state.transactions?.length) {
          nextTransactions = ([] as GeneralLedgerTransaction[]).concat(
            state.transactions,
          );
        }

        nextTransactions.unshift({
          ...action.payload.data,
          accountId: action.payload.data.debitAccountId,
          deletedAt: action.payload.data?.deletedAt || '',
          url: generatePath(ROUTES.GENERAL_LEDGER_READ, {
            transactionId: action.payload.data.id,
          }),
        });

        state.transactions = nextTransactions;

        if (state.selectedTake) {
          state.selectedTake += 1;
        }
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.getGeneralLedgerListStatuses
        .matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            status: GeneralLedgerStatus;
            id: string;
          }>
        >,
      ) => {
        const { edges } = action.payload.data;
        const nextTransactions = state?.transactions?.map((item) => {
          const result = { ...item };

          const currentTransactionStatus = edges.find(
            (transaction) => transaction.node.id === result.id,
          );

          if (
            currentTransactionStatus &&
            currentTransactionStatus.node.status !== result.status
          ) {
            result.status = currentTransactionStatus.node.status;
          }

          return result;
        });

        state.transactions = nextTransactions;
      },
    );
    builder.addMatcher(
      glTransactionsListApi.endpoints.getTransactionsAccountsCategories
        .matchFulfilled,
      (state, action: PayloadAction<Response<Category>>) => {
        state.accountCategories = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
  },
});

export const { clearGeneralLedgerListState, setSelectedTake } =
  generalLedgerListSlice.actions;

export default generalLedgerListSlice.reducer;
